import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Button,
  Stack,
  ListItemButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ArchivedChip, RestoreOrDeleteButton } from '../../common/components';
import type { Company } from '../../../services/companyService';
import CompanyFilters from './CompanyFilters';

interface CompanyListProps {
  companies: Company[];
  onCreateCompany: () => void;
  onDeleteCompany: (companyId: number) => void;
  onRestoreCompany: (companyId: number) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  showOnlyActive: boolean;
  onShowOnlyActiveChange: (value: boolean) => void;
}

const CompanyList: React.FC<CompanyListProps> = ({
  companies,
  onCreateCompany,
  onDeleteCompany,
  onRestoreCompany,
  showFilters,
  onToggleFilters,
  showOnlyActive,
  onShowOnlyActiveChange,
}) => {
  const navigate = useNavigate();

  const handleCompanyClick = (companyId: number) => {
    navigate(`/companies/${companyId}/home`);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Ваші компанії
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Tooltip title="Показати/сховати фільтри">
            <IconButton
              size="small"
              onClick={onToggleFilters}
              color={showFilters || !showOnlyActive ? 'primary' : 'default'}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateCompany}
          >
            Додати
          </Button>
        </Stack>
      </Box>

      <CompanyFilters
        show={showFilters}
        showOnlyActive={showOnlyActive}
        onShowOnlyActiveChange={onShowOnlyActiveChange}
      />

      {companies.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Компанії не знайдено. Цього не має траплятись — вас мало б перенаправити на створення компанії.
        </Typography>
      ) : (
        <List>
          {companies.map((company) => {
            const isArchived = !!company.deactivatedDate;

            return (
              <Paper
                key={company.id}
                elevation={1}
                sx={{
                  mb: 1.5,
                  '&:hover': { elevation: 3 },
                }}
              >
                <ListItem
                  disablePadding
                  secondaryAction={
                    <RestoreOrDeleteButton
                      archived={isArchived}
                      edge="end"
                      onDelete={() => onDeleteCompany(company.id)}
                      onRestore={() => onRestoreCompany(company.id)}
                    />
                  }
                >
                  <ListItemButton
                    onClick={() => handleCompanyClick(company.id)}
                    disabled={isArchived}
                    sx={{ py: { xs: 1.25, sm: 2 } }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="h6"
                            component="span"
                            color={isArchived ? 'text.secondary' : 'text.primary'}
                          >
                            {company.name}
                          </Typography>
                          {isArchived && <ArchivedChip />}
                        </Stack>
                      }
                    />
                    {!isArchived && <ChevronRightIcon color="action" />}
                  </ListItemButton>
                </ListItem>
              </Paper>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default CompanyList;